import { Link } from 'react-router-dom'
import { useState } from 'react'
import { dayGap, isSabbath, ordinalWord } from '../calendar'
import type { PublishedDay, Reckoning } from '../calendar'
import { CountedMonth } from '../components/Masthead'
import { feastText } from './feasts'
import { whenWords } from './Ribbon'
import { useToast } from './Toast'

/**
 * The sidebar, and the ambient card at the top of the feed.
 *
 * Every number here is read off the published calendar. The widgets are the Torahly shell's
 * Appointed Times and Parts of the Day, with Covellah's facts in them.
 */

/** The next Sabbath and the next three feasts, as rows with a day tile each. */
export function AppointedTimes({
  reckoning,
  onMonth,
  compact,
}: {
  reckoning: Reckoning | null
  onMonth?: () => void
  compact?: boolean
}) {
  const toast = useToast()
  if (!reckoning) return null
  const { today, nextSabbath, upcomingFeasts } = reckoning

  const rows: { day: PublishedDay; name: string; sub: string; kind: 'sabbath' | 'feast' }[] = []
  if (nextSabbath) {
    rows.push({ day: nextSabbath, name: 'Sabbath', sub: 'Seventh day · Exodus 20:8–11', kind: 'sabbath' })
  }
  for (const feast of upcomingFeasts) {
    const text = feastText(feast.f ?? '')
    rows.push({
      day: feast,
      name: feast.f ?? '',
      sub: [text?.hebrew, text?.cite].filter(Boolean).join(' · '),
      kind: 'feast',
    })
  }

  // Whichever comes first leads, so a feast the day after tomorrow is not listed under a later Sabbath.
  rows.sort((a, b) => a.day.d.localeCompare(b.day.d))

  return (
    <section className={compact ? 'card is-compact' : 'card'}>
      <div className="card-head">
        <h3 className="card-title">
          <i className="ph-bold ph-calendar-star" aria-hidden="true" />
          Appointed times
        </h3>
        {onMonth && (
          <button className="card-link label" type="button" onClick={onMonth}>
            The month
          </button>
        )}
      </div>
      <div className="rows-list">
        {rows.map((row, index) => {
          const gap = dayGap(today.d, row.day.d)
          return (
            <div
              key={`${row.kind}-${row.day.d}`}
              className={['row-item', index === 0 ? 'is-next' : '', row.kind === 'feast' ? 'is-feast' : ''].join(' ')}
            >
              <div className="row-left">
                <div className="day-tile">
                  <span className="tile-n">{row.day.dy}</span>
                  <span className="tile-m">{shortMonth(row.day.m)}</span>
                </div>
                <div>
                  <div className="row-name">{row.name}</div>
                  <div className="row-sub">{row.sub}</div>
                </div>
              </div>
              <span className={index === 0 ? 'row-when is-gold' : index === 1 ? 'row-when is-strong' : 'row-when'}>
                {capitalise(whenWords(gap))}
              </span>
            </div>
          )
        })}
      </div>
      <button
        className="widget-btn"
        type="button"
        onClick={() => toast('Feast dates and Sabbath times are free, and always will be')}
      >
        <i className="ph-bold ph-calendar-plus" aria-hidden="true" />
        <span>Add the feasts to my calendar</span>
      </button>
    </section>
  )
}

/** The month counted out as weeks, with a meter for how far through it she is. */
export function MonthWidget({ reckoning, compact }: { reckoning: Reckoning | null; compact?: boolean }) {
  if (!reckoning) return null
  const { today, month } = reckoning
  const counted = today.dy
  const total = month.length
  const pct = Math.round((counted / total) * 100)
  const sabbathsLeft = month.filter((day) => day.d > today.d && isSabbath(day)).length

  return (
    <section className={compact ? 'card is-compact' : 'card'}>
      <div className="card-head">
        <h3 className="card-title">
          <i className="ph-bold ph-moon" aria-hidden="true" />
          The {ordinalWord(today.m)} month
        </h3>
        <span className="card-note">
          Day {counted} of {total}
        </span>
      </div>
      <div className="meter" style={{ marginTop: 16 }} role="progressbar" aria-valuenow={counted} aria-valuemax={total}>
        <div className="meter-fill" style={{ width: `${pct}%` }}>
          {counted} counted
        </div>
      </div>
      <CountedMonth month={month} today={today} />
      <p className="quote-well">
        “And God said, Let there be lights in the firmament of the heaven to divide the day from the
        night; and let them be for signs, and for seasons, and for days, and years.”
      </p>
      <div className="quote-ref">Genesis 1:14 · KJV</div>
      <p className="widget-text">
        {sabbathsLeft === 0
          ? 'No Sabbath left in this month.'
          : sabbathsLeft === 1
            ? 'One Sabbath left in this month.'
            : `${sabbathsLeft} Sabbaths left in this month.`}
      </p>
    </section>
  )
}

/** The private portal, offered where the Torahly shell offers its study companion. */
export function PhoneWidget() {
  return (
    <section className="card card-gradient">
      <h3 className="card-title is-olive">
        <i className="ph-bold ph-device-mobile-camera" aria-hidden="true" />
        My phone
      </h3>
      <p className="widget-text">
        Read your cycle, journal and pregnancy records on this screen. The server never holds them;
        this tab borrows a copy until you close it.
      </p>
      <Link className="widget-btn is-rose" to="/phone">
        <i className="ph-bold ph-qr-code" aria-hidden="true" />
        <span>Pair my phone</span>
      </Link>
    </section>
  )
}

/**
 * The ambient card: the next appointed times, in the text's own words.
 *
 * A carousel over the next Sabbath and the next feasts, each quoted from Leviticus 23 and cited,
 * so the day ahead is introduced by the passage that appoints it.
 */
export function AppointedTimeCard({ reckoning }: { reckoning: Reckoning | null }) {
  const toast = useToast()
  const [index, setIndex] = useState(0)
  if (!reckoning) return null

  const { today, nextSabbath, upcomingFeasts } = reckoning
  const slides = [
    ...upcomingFeasts.map((day) => ({ day, name: day.f ?? '' })),
    ...(nextSabbath ? [{ day: nextSabbath, name: 'Sabbath' }] : []),
  ].filter((slide) => feastText(slide.name))
  if (slides.length === 0) return null

  const current = slides[index % slides.length]
  if (!current) return null
  const text = feastText(current.name)
  if (!text) return null
  const gap = dayGap(today.d, current.day.d)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`“${text.quote}” ${text.cite}`)
      toast('Copied')
    } catch {
      toast('Could not copy')
    }
  }

  return (
    <section className="card card-ambient">
      <div className="glow-a" aria-hidden="true" />
      <div className="glow-b" aria-hidden="true" />
      <div className="card-head">
        <div className="card-kicker">
          <span className="live" aria-hidden="true" />
          <span className="label">The appointed time ahead</span>
          <span>
            • {current.name}, {whenWords(gap)}
          </span>
        </div>
        {slides.length > 1 && (
          <div className="pager">
            <button
              className="pager-btn"
              type="button"
              aria-label="Previous"
              onClick={() => setIndex((index - 1 + slides.length) % slides.length)}
            >
              <i className="ph-bold ph-caret-left" aria-hidden="true" />
            </button>
            <span className="pager-n">
              {(index % slides.length) + 1} of {slides.length}
            </span>
            <button
              className="pager-btn"
              type="button"
              aria-label="Next"
              onClick={() => setIndex((index + 1) % slides.length)}
            >
              <i className="ph-bold ph-caret-right" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
      <blockquote className="scripture">“{text.quote}”</blockquote>
      <div className="scripture-foot">
        <div>
          <span className="cite">{text.cite}</span>
          <span> • KJV</span>
          {text.hebrew && <span> • {text.hebrew}</span>}
        </div>
        <div className="acts">
          <button className="btn-ghost btn-sm" type="button" onClick={() => void copy()}>
            <i className="ph-bold ph-copy" aria-hidden="true" />
            Copy
          </button>
        </div>
      </div>
    </section>
  )
}

function shortMonth(m: number): string {
  const suffix = m === 1 ? 'st' : m === 2 ? 'nd' : m === 3 ? 'rd' : 'th'
  return `${m}${suffix} mo`
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}
